// Change template… (section 6.1, W24): heirs first, every other document template below; the dry run shows the parameter
// diff and the references that would break before anything is committed; each document is then one commit of its own.
import { useEffect, useMemo, useState } from 'react';
import { ApiError } from '../../api/client';
import { ops } from '../../api/operations';
import type { ChangeTemplateResult, Document } from '../../api/types';
import { Button } from '../../ui/Button';
import { Badge } from '../../ui/Chip';
import { useConfirm } from '../../ui/ConfirmDialog';
import { Field } from '../../ui/Field';
import { AlertCircle, AlertTriangle } from '../../ui/icons';
import { Checkbox, Select } from '../../ui/inputs';
import { Modal } from '../../ui/Modal';
import { useToasts } from '../../ui/Toast';
import { entityKey } from '../../workspace/Workspace';
import { changeTemplateChoices } from '../actions';
import { useWidgetEnv } from '../widgets/context';

export function ChangeTemplateDialog({ docs, onClose, onDone }: { docs: Document[]; onClose: () => void; onDone?: (changed: number) => void }) {
  const { catalog, client, org, game, branch, workspace } = useWidgetEnv();
  const toasts = useToasts();
  const confirm = useConfirm();
  const from = docs[0]?.template ?? '';
  const choices = useMemo(() => changeTemplateChoices(catalog, from), [catalog, from]);
  const [target, setTarget] = useState<string>(choices.heirs[0]?.name ?? '');
  const [dropValues, setDropValues] = useState(false);
  const [previews, setPreviews] = useState<Map<string, ChangeTemplateResult | ApiError>>(new Map());
  const [running, setRunning] = useState(false);
  const [progress, setProgress] = useState(0);
  const dirty = docs.filter((d) => workspace.getDraft(entityKey('document', d.id)));
  const mixed = docs.some((d) => d.template !== from);

  useEffect(() => {
    if (!target || dirty.length > 0 || mixed) return;
    let alive = true;
    setPreviews(new Map());
    (async () => {
      const next = new Map<string, ChangeTemplateResult | ApiError>();
      for (const d of docs) {
        try {
          next.set(d.id, await client.data(ops.changeDocumentTemplate, { path: { org, game, branch, document: d.id }, body: { template: target, dropValues, dryRun: true }, ifMatch: Number(d.version) }));
        } catch (e) {
          next.set(d.id, e instanceof ApiError ? e : new ApiError(0, 'unavailable', String(e), ''));
        }
        if (!alive) return;
        setPreviews(new Map(next));
      }
    })();
    return () => {
      alive = false;
    };
  }, [target, dropValues, docs, client, org, game, branch, dirty.length, mixed]);

  const summary = useMemo(() => {
    const kept = new Set<string>();
    const converted = new Set<string>();
    const dropped = new Set<string>();
    const added = new Set<string>();
    let incompatible = 0;
    let conflicts = 0;
    let errors = 0;
    for (const r of previews.values()) {
      if (r instanceof ApiError) {
        if (r.status === 409) conflicts++;
        else errors++;
        continue;
      }
      r.kept.forEach((k) => kept.add(k));
      r.converted.forEach((k) => converted.add(k));
      r.dropped.forEach((k) => dropped.add(k));
      r.added.forEach((k) => added.add(k));
      incompatible += r.incompatibleReferences.length;
    }
    return { kept: [...kept], converted: [...converted], dropped: [...dropped], added: [...added], incompatible, conflicts, errors };
  }, [previews]);
  const ready = previews.size === docs.length && summary.errors === 0 && summary.conflicts === 0 && summary.incompatible === 0 && dirty.length === 0 && !mixed;

  const proceed = async () => {
    const ok = await confirm({
      title: `Change the template of ${docs.length} document${docs.length === 1 ? '' : 's'}?`,
      body: `${summary.dropped.length > 0 ? `The values of ${summary.dropped.length} parameter${summary.dropped.length === 1 ? '' : 's'} (${summary.dropped.join(', ')}) are lost. ` : ''}Each document becomes a commit of its own at once; this is not a staged change.`,
      confirmLabel: 'Change template',
      tone: summary.dropped.length > 0 ? 'danger' : 'primary',
    });
    if (!ok) return;
    setRunning(true);
    let changed = 0;
    let lastCommit: string | null = null;
    for (const d of docs) {
      try {
        const result = await client.call(ops.changeDocumentTemplate, { path: { org, game, branch, document: d.id }, body: { template: target, dropValues, dryRun: false }, ifMatch: Number(d.version) });
        changed++;
        lastCommit = result.data.commit ?? result.commit;
      } catch (e) {
        toasts.error(`${catalog.label(d)}: ${e instanceof ApiError ? e.message : String(e)}`, { requestId: e instanceof ApiError ? e.requestId : undefined });
      }
      setProgress(changed);
    }
    if (lastCommit) workspace.setHead(lastCommit);
    catalog.resolver.invalidate();
    toasts.success(`${changed} of ${docs.length} document${docs.length === 1 ? '' : 's'} moved to ${target}.`);
    setRunning(false);
    onDone?.(changed);
    onClose();
  };

  const option = (t: { name: string; displayName?: string | null }) => (
    <option key={t.name} value={t.name}>
      {t.displayName ?? t.name} ({t.name})
    </option>
  );
  return (
    <Modal
      title={`Change template of ${docs.length === 1 ? catalog.label(docs[0]!) : `${docs.length} documents`}`}
      open
      onClose={onClose}
      size="m"
      footer={
        <>
          <span className="spacer" />
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" onClick={() => void proceed()} disabled={!ready || running} busy={running}>
            Proceed{running ? ` (${progress}/${docs.length})` : ''}
          </Button>
        </>
      }
    >
      <div className="stack dialog-form">
        {mixed && (
          <div className="banner banner-danger">
            <AlertCircle className="banner-icon" aria-hidden="true" />
            <div className="banner-body">The selected documents have different templates; change them one template at a time.</div>
          </div>
        )}
        {dirty.length > 0 && (
          <div className="banner banner-warning" role="alert">
            <AlertTriangle className="banner-icon" aria-hidden="true" />
            <div className="banner-body">{dirty.length} of the documents have unsaved changes: save or discard them first (a template change is a commit, not a staged edit).</div>
          </div>
        )}
        <Field label="New template" help="Heirs of the current template first; the dry run below shows what each parameter becomes.">
          <Select value={target} onChange={(e) => setTarget(e.target.value)} aria-label="New template">
            {choices.heirs.length > 0 && <optgroup label={`Heirs of ${from}`}>{choices.heirs.map(option)}</optgroup>}
            <optgroup label="Other templates">{choices.others.map(option)}</optgroup>
          </Select>
        </Field>
        {target && previews.size < docs.length && dirty.length === 0 && !mixed && (
          <span className="loading-line" role="status">
            <span className="spinner" aria-hidden="true" />
            Comparing {previews.size}/{docs.length}…
          </span>
        )}
        {previews.size > 0 && (
          <div className="diff-summary">
            <DiffRow tone="success" title="Kept" names={summary.kept} />
            <DiffRow tone="info" title="Converted" names={summary.converted} />
            <DiffRow tone="danger" title="Dropped (values lost)" names={summary.dropped} />
            <DiffRow tone="neutral" title="Added (defaults)" names={summary.added} />
            {summary.incompatible > 0 && (
              <p className="field-error">
                {summary.incompatible} reference{summary.incompatible === 1 ? '' : 's'} to these documents require{summary.incompatible === 1 ? 's' : ''} a {from}; the new template does not satisfy them, so the change is refused.
              </p>
            )}
            {summary.conflicts > 0 && (
              <div className="banner banner-warning">
                <AlertTriangle className="banner-icon" aria-hidden="true" />
                <div className="banner-body stack">
                  <span>
                    {summary.conflicts} document{summary.conflicts === 1 ? '' : 's'} hold values the new type cannot carry over.
                  </span>
                  <Checkbox label="Drop the values that cannot be converted" checked={dropValues} onChange={(e) => setDropValues(e.target.checked)} />
                </div>
              </div>
            )}
            {summary.errors > 0 && (
              <p className="field-error">
                {Array.from(previews.values()).filter((r): r is ApiError => r instanceof ApiError && r.status !== 409).map((r) => r.message)[0]}
              </p>
            )}
            <p className="muted small">Affected documents: {docs.length}. Ids, tags, export and references stay.</p>
          </div>
        )}
      </div>
    </Modal>
  );
}

function DiffRow({ tone, title, names }: { tone: 'success' | 'info' | 'danger' | 'neutral'; title: string; names: string[] }) {
  if (names.length === 0) return null;
  return (
    <div className="row wrap">
      <Badge tone={tone}>{title}</Badge>
      <span className="small">{names.join(', ')}</span>
    </div>
  );
}
